// src/domains/repairs/hooks/useRepairsUnified.ts
import { useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useEntityList } from '@/hooks/useEntityList';
import { useEntityOperations } from '@/hooks/common/useEntityOperations';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Repair, RepairCreate, RepairUpdate, RepairFilters, DbRepair } from '@/types/entities/repair';
import { dbRepairToRepair, repairCreateToDbRepairInsert, repairUpdateToDbRepairUpdate } from '@/utils/mappers/repairMappers';
import { TABLE_NAMES } from '@/constants/tableNames';

const TABLE_NAME = TABLE_NAMES.repairs;
const QUERY_KEY = [TABLE_NAME];

export function useRepairsUnified(filters?: RepairFilters): {
  repairs: Repair[];
  allRepairs: Repair[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  createRepair: (repairData: RepairCreate) => Promise<Repair | undefined>;
  updateRepair: (id: string, updates: RepairUpdate) => Promise<Repair | undefined>;
  deleteRepair: (id: string) => Promise<boolean>;
  updateRepairStatus: (repairId: string, status: string) => Promise<Repair | undefined>;
  invalidateRepairs: () => void;
  isCreating: boolean;
  isUpdating: boolean;
} {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { 
    data: repairs, 
    isLoading: isListLoading, 
    error, 
    refetch 
  } = useEntityList<Repair, typeof TABLE_NAME>({
    tableName: TABLE_NAME,
    queryKey: [...QUERY_KEY, filters?.status, filters?.search],
    dbEntityToEntityMapper: dbRepairToRepair,
    filters: {
      status: filters?.status,
      client_id: filters?.clientId,
      urgent_repair: filters?.urgentRepair,
    },
  });

  const { 
    create, 
    update, 
    delete: deleteEntity, 
    loading: isMutating 
  } = useEntityOperations(TABLE_NAME, 'Ремонт');

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }, [queryClient]);

  const createRepair = useCallback(async (repairData: RepairCreate) => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    try {
      const dbData = repairCreateToDbRepairInsert(repairData, user.id);
      const result = await create(dbData);
      invalidateQueries();
      toast.success('Ремонт успешно создан');
      return dbRepairToRepair(result as DbRepair);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error creating repair:', err);
      toast.error('Не удалось создать ремонт');
      throw new Error(message);
    }
  }, [user?.id, create, invalidateQueries]);

  const updateRepair = useCallback(async (id: string, updates: RepairUpdate) => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    try {
      const dbUpdates = repairUpdateToDbRepairUpdate(updates);
      const result = await update(id, dbUpdates);
      invalidateQueries();
      toast.success('Ремонт успешно обновлен');
      return dbRepairToRepair(result as DbRepair);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error updating repair:', err);
      toast.error('Не удалось обновить ремонт');
      throw new Error(message);
    }
  }, [user?.id, update, invalidateQueries]);

  const deleteRepair = useCallback(async (id: string) => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return false;
    }

    try {
      await deleteEntity(id);
      invalidateQueries();
      toast.success('Ремонт успешно удален');
      return true;
    } catch (err: unknown) {
      console.error('Error deleting repair:', err);
      toast.error('Не удалось удалить ремонт');
      return false;
    }
  }, [user?.id, deleteEntity, invalidateQueries]);

  const updateRepairStatus = useCallback(async (repairId: string, status: string) => {
    const updateData: RepairUpdate = { 
      status,
      ...(status === 'completed' && { completedAt: new Date() })
    };
    
    return updateRepair(repairId, updateData);
  }, [updateRepair]);

  // Фильтрация по поисковому запросу на клиенте (для совместимости)
  const filteredRepairs = useMemo(() => {
    if (!repairs || !filters?.search) return repairs || [];
    
    const searchTerm = filters.search.toLowerCase();
    return repairs.filter(repair => {
      const itemNameMatch = repair.itemName?.toLowerCase().includes(searchTerm) ?? false;
      const issueDescriptionMatch = repair.issueDescription?.toLowerCase().includes(searchTerm) ?? false;
      const clientNameMatch = repair.clientName?.toLowerCase().includes(searchTerm) ?? false;
      return itemNameMatch || issueDescriptionMatch || clientNameMatch;
    });
  }, [repairs, filters?.search]);

  return {
    repairs: filteredRepairs,
    allRepairs: repairs || [],
    loading: isListLoading || isMutating,
    error,
    refetch,
    createRepair,
    updateRepair,
    deleteRepair,
    updateRepairStatus,
    invalidateRepairs: invalidateQueries,
    isCreating: isMutating,
    isUpdating: isMutating,
  };
}
